/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * LanguageSwitcher Component
 *
 * --- REWRITE SUMMARY (V2 - ICON REFACTOR) ---
 * - MISSION: To fix the missing icons by replacing the bloated Font Awesome
 * library with lightweight, tree-shakable React components.
 *
 * - THE FIX (Component Replacement):
 * 1.  NEW IMPORTS: The `FaGlobe` and `FaChevronDown` icon components are
 * now imported directly from the `react-icons/fa` library.
 * 2.  COMPONENT REPLACEMENT: All instances of `<i className="...">` have
 * been replaced with their corresponding React icon components (e.g.,
 * `<FaGlobe />`).
 *
 * - RESULT: This change eliminates the dependency on the old Font Awesome
 * CSS for this component. The icons are now tiny, self-contained SVGs,
 * which will render correctly and contribute to fixing the mobile
 * performance issue.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaGlobe, FaChevronDown } from 'react-icons/fa';

const LanguageSwitcher = ({ isMobile = false }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const currentLanguageCode = i18n.language.split('-')[0];

  const changeLanguage = useCallback((lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target) &&
        btnRef.current && !btnRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const supportedLngs = i18n.options.supportedLngs || [];

  const languageOptions = supportedLngs
    .filter(lng => lng !== 'dev' && lng !== 'cimode' && lng !== 'ru')
    .map((lng) => (
      <button
        key={lng}
        onClick={() => changeLanguage(lng)}
        className="block w-full px-4 py-2 text-sm text-left text-white transition-colors rounded-md hover:bg-muted rtl:text-right"
        role="menuitem"
      >
        {t(`languages.${lng}`)} ({lng.toUpperCase()})
      </button>
    ));

  if (isMobile) {
    return (
      <div className="pt-4 mt-4 border-t border-muted">
        <h3 className="px-1 pb-2 text-xs font-bold tracking-wider text-muted-foreground uppercase">Language</h3>
        <div className="relative">
          <button
            ref={btnRef}
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-between w-full p-3 text-white transition-colors rounded-md bg-muted hover:bg-muted/70"
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-label="Select language"
          >
            <span className="font-medium">{t(`languages.${currentLanguageCode}`)} ({currentLanguageCode.toUpperCase()})</span>
            <FaChevronDown className={`text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {isOpen && (
            <div ref={menuRef} className="absolute bottom-full z-20 w-full p-1 mb-2 overflow-y-auto bg-card border border-muted rounded-md shadow-lg max-h-60 ltr:left-0 rtl:right-0 ltr:origin-bottom-left rtl:origin-bottom-right" role="menu">
              {languageOptions}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center h-10 px-3 text-white transition-colors rounded-full hover:bg-white/10"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Select language"
      >
        <FaGlobe aria-hidden="true" />
        <span className="mx-1.5 text-sm font-medium">{currentLanguageCode.toUpperCase()}</span>
        <FaChevronDown className={`text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <div ref={menuRef} className="absolute z-20 w-56 py-1 mt-2 overflow-y-auto bg-card border border-muted rounded-md shadow-lg ltr:right-0 rtl:left-0 ltr:origin-top-right rtl:origin-top-left" role="menu">
          {languageOptions}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
